import React, { useState, useMemo, useEffect } from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useClient } from '../../ClientContext';
// Formatting only. The figures come from the warehouse now; computing them a
// second time in the browser is how a dashboard tile and a chat answer end up
// disagreeing about the same week.
import {
  gbp, num, pct, fmt, shortDate, METRIC_LABELS, LOWER_IS_BETTER,
} from '../../reporting/metrics';
import {
  loadDashboard, loadCampaignTrends, TILE_METRICS, TABLE_METRICS,
} from '../../reporting/dashboardData';
import { AuthError } from '../../api';
import { token } from '../../tokens';
import Icon from '../../components/Icon';
import Sparkline from '../../components/Sparkline';
import './reporting.css';

/**
 * Reporting Dashboard.
 *
 * The KPI row displayed four numbers above a chart that always plotted the
 * same two. Selecting a card now decides what the chart plots, which turns a
 * decorative row into navigation and means one large chart does the work of
 * four small ones. The previous period is drawn behind as a ghost, because a
 * number with no shape beside it is not a trend.
 *
 * The "illustrative data" warning has moved above the figures. It used to sit
 * at 11.5px below the fold, which meant anyone demoing this had already read
 * every number aloud before finding out none of them were real.
 */

const RANGES = [7, 30, 90];
// The registry's names, not the generated data's. `leads` and `clicks` were
// never metrics here: the warehouse has `conversions` and `link_clicks`, and
// Meta's wider click count is deliberately not comparable to Google's.
const PLOTTABLE = TILE_METRICS;
const TABLE_COLUMNS = TABLE_METRICS;

/** A metric's label, the client's own definition winning over the constant. */
const labelOf = (labels, key) => (labels && labels[key]) || METRIC_LABELS[key] || key;

// WHICH ACCOUNTS ARE IN VIEW.
//
// All of them by default: an advertiser's Google and Meta accounts reported
// as one, which is the thing this product is for. The other two narrow to a
// single platform for someone who wants to look at that account on its own.
//
// Not a roll-up across advertisers — those never combine, and each has its own
// warehouse.
const ACCOUNT_VIEWS = [
  { label: 'All accounts', platform: null },
  { label: 'Google', platform: 'google' },
  { label: 'Meta', platform: 'meta' },
];

export default function ReportingDashboard() {
  const { selectedId, selected, isAll } = useClient();

  const [days, setDays] = useState(30);
  const [metric, setMetric] = useState('conversions');
  const [sortKey, setSortKey] = useState('spend');
  // null means every account this advertiser has — the combined view, which is
  // the default and the point of the product. The alternatives narrow to one
  // platform's account.
  const [platform, setPlatform] = useState(null);

  const [data, setData] = useState(null);
  // The sparkline series, and which selection they belong to. Not `series`,
  // which is this page's chart data - the build caught that collision.
  const [sparklines, setSparklines] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // ONE ADVERTISER AT A TIME, DELIBERATELY.
  //
  // A client here is one advertiser, and this page already rolls up that
  // advertiser's Google and Meta accounts - gold does it before these queries
  // run. What it does not do is roll up across advertisers: each has its own
  // dataset, nothing can read two of them, and an agency looking at two
  // advertisers wants two dashboards rather than their sum.
  const oneAdvertiser = !isAll && Boolean(selectedId);
  // Everything that changes which series are correct. Not sortKey, which
  // changes only their order.
  const sparklineKey = `${selectedId}|${days}|${platform || ''}`;

  useEffect(() => {
    if (!oneAdvertiser) { setData(null); return undefined; }
    let live = true;
    setBusy(true);
    setError(null);
    loadDashboard({ clientId: selectedId, days, sortKey, platform })
      .then((loaded) => { if (live) { setData(loaded); setBusy(false); } })
      .catch((e) => {
        if (!live) return;
        setBusy(false);
        setError(e instanceof AuthError
          ? e.message
          : (e.message || 'The figures could not be loaded.'));
      });
    return () => { live = false; };
  }, [oneAdvertiser, selectedId, days, sortKey, platform]);

  // THE SPARKLINES ARRIVE AFTER THE PAGE DOES.
  //
  // A thousand rows of campaign x date, feeding a 60-pixel line in each table
  // row. It used to be inside the same Promise.all as the tiles and the
  // chart, so everything above the table waited on the largest and least
  // urgent query on the page.
  //
  // Not keyed on sortKey: the ordering of the table changes which rows are on
  // screen, not which series each row draws, and refetching a thousand rows
  // to re-sort a table the server already sorted would undo the point.
  //
  // HELD SEPARATELY, AND STAMPED WITH THE WINDOW IT IS FOR.
  //
  // The first version merged the series into data, which loses them whenever
  // they arrive first: the merge skipped a null data, and the main load then
  // overwrote the field with an empty map, so the sparklines stayed blank
  // until the window changed. The two requests race by design - that is the
  // whole point of splitting them - so neither may depend on landing second.
  //
  // The stamp is what keeps a series off the wrong table. Switching
  // advertiser mid-flight leaves a response in the air that is correct for an
  // account nobody is looking at, and a sparkline under the wrong campaign is
  // worse than no sparkline.
  useEffect(() => {
    if (!oneAdvertiser) { setSparklines(null); return undefined; }
    let live = true;
    loadCampaignTrends({ clientId: selectedId, days, platform })
      .then(({ trends, trendsComplete }) => {
        if (live) setSparklines({ key: sparklineKey, trends, trendsComplete });
      })
      .catch(() => {
        // A sparkline is an ornament. Losing it is not worth an error on a
        // page whose figures are all correct and already on screen.
        if (live) {
          setSparklines({ key: sparklineKey, trends: new Map(), trendsComplete: false });
        }
      });
    return () => { live = false; };
  }, [oneAdvertiser, selectedId, days, platform, sparklineKey]);

  const labels = data?.labels;
  const t = data?.totals || null;
  const byDate = data?.byDate || [];
  const prevByDate = data?.prevByDate || [];
  const byCampaign = data?.byCampaign || [];

  // Supplied by the server, not subtracted here. The page used to compute this
  // and so did the narrator, in different places, from different queries.
  const delta = (k) => data?.change?.[k]?.percent ?? null;

  // Zip the two windows by position. They are the same length by construction,
  // so index is the honest join — the dates differ, which is the point.
  const series = useMemo(() => byDate.map((row, i) => ({
    ...row,
    prev: prevByDate[i]?.[metric] ?? null,
  })), [byDate, prevByDate, metric]);

  const colours = {
    line: token('--chart-1', '#2D6B50'),
    ghost: token('--chart-ghost', '#B9BFB9'),
    grid: token('--chart-grid', '#E5E7EB'),
    axis: token('--chart-axis', '#6B7280'),
  };

  const sortMax = Math.max(...byCampaign.map((r) => r[sortKey] || 0), 0);
  const scopeLabel = selected?.name || 'Selected client';

  const axisFormat = (v) => {
    if (metric === 'spend') return v >= 1000 ? `£${Math.round(v / 1000)}k` : `£${Math.round(v)}`;
    if (metric === 'cpa' || metric === 'cpc' || metric === 'cpm') return `£${Math.round(v)}`;
    if (metric === 'roas') return `${v.toFixed(1)}×`;
    return num(v);
  };

  // ── The states a generated dashboard never had ──────────────────────────
  //
  // Invented data is instant, always present and never refused. Real data is
  // none of those, and a page that assumes otherwise renders undefined into a
  // tile.

  if (!oneAdvertiser) {
    return (
      <>
        <div className="page-header">
          <h1 className="page-title">Reporting Dashboard</h1>
        </div>
        <div className="banner">
          <Icon name="alert" size={16} strokeWidth={2.2} />
          <span>
            Choose an advertiser to report on. Their Google and Meta accounts
            are reported together by default, and the switch above the figures
            narrows to one of them. Advertisers are never combined with each
            other — each has its own warehouse.
          </span>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="page-header">
          <h1 className="page-title">Reporting Dashboard</h1>
        </div>
        <div className="banner warn">
          <Icon name="alert" size={16} strokeWidth={2.2} />
          <span>{error}</span>
        </div>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <div className="page-header split">
          <div>
            <h1 className="page-title">Reporting Dashboard</h1>
            <p className="page-subtitle">{scopeLabel} · loading the last {days} days…</p>
          </div>
        </div>
      </>
    );
  }

  // Loaded, and nothing delivered. This used to share the loading gate above
  // (`!data || !t`), so an account with no delivery said "loading…" forever -
  // which reads as broken rather than as the true answer, which is "nothing
  // has run".
  if (!t) {
    return (
      <>
        <div className="page-header split">
          <div>
            <h1 className="page-title">Reporting Dashboard</h1>
            <p className="page-subtitle">{scopeLabel} · last {days} days</p>
          </div>
        </div>
        <div className="card">
          <div className="empty">
            <h3>No delivery in the last {days} days</h3>
            <p>
              {scopeLabel}&apos;s connected accounts recorded no impressions or spend in this
              window — campaigns may be paused or not yet live. Figures appear here the
              morning after the first day of delivery.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header split">
        <div>
          <h1 className="page-title">Reporting Dashboard</h1>
          <p className="page-subtitle">
            {scopeLabel} · {platform ? `${platform === 'meta' ? 'Meta' : 'Google'} only`
              : 'Google and Meta'} · {data.range.start} to {data.range.end} · GBP
          </p>
        </div>
        <div className="hdr-actions">
          <div className="seg" role="group" aria-label="Accounts">
            {ACCOUNT_VIEWS.map((view) => (
              <button
                key={view.label}
                aria-pressed={platform === view.platform}
                onClick={() => setPlatform(view.platform)}
              >
                {view.label}
              </button>
            ))}
          </div>
          <div className="seg" role="group" aria-label="Date range">
            {RANGES.map((n) => (
              <button key={n} aria-pressed={days === n} onClick={() => setDays(n)}>{n}D</button>
            ))}
          </div>
        </div>
      </div>

      {/*
        The warning banner is gone: these figures are this advertiser's own.
        What remains worth saying is said per figure — the caveats below carry
        the conversion mapping's confirmed state and the maturity of recent
        conversions, which is the honest version of a blanket disclaimer.
      */}
      {busy && <div className="banner"><span>Refreshing…</span></div>}

      <div className="metric-row">
        {PLOTTABLE.map((key) => {
          const d = delta(key);
          const better = d == null ? null : (LOWER_IS_BETTER.has(key) ? d < 0 : d > 0);
          return (
            <button
              key={key}
              className="metric"
              aria-pressed={metric === key}
              onClick={() => setMetric(key)}
            >
              <span className="metric-plot">Plotted</span>
              <div className="kpi-label">{labelOf(labels, key)}</div>
              <div className="kpi-value">{fmt(key, t[key])}</div>
              {d != null && Number.isFinite(d) && (
                <div className={`delta ${better ? 'good' : 'bad'}`}>
                  <Icon name={d > 0 ? 'up' : 'down'} size={11} strokeWidth={3} />
                  {Math.abs(d).toFixed(1)}%
                  <span className="note">vs previous {days} days</span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/*
        Below the figures, not above them.

        These were at the top of the page and four bullets pushed every number
        below the fold - which reads as a warning about the dashboard rather
        than a note on the numbers in it. The figures are this advertiser's
        own; what the caveats say is how far to trust a conversion count that
        is still filling in, and a mapping the client has not confirmed.
      */}
      {(data.caveats || []).length > 0 && (
        <ul className="ins-caveats dash-caveats">
          {data.caveats.map((caveat, i) => <li key={i}>{caveat}</li>)}
        </ul>
      )}

      <div className="card" style={{ marginBottom: 'var(--space-5)' }}>
        <div className="card-header">
          <span className="card-title">{labelOf(labels, metric)} · daily</span>
          <span className="ghost-key">
            <svg width="26" height="8" aria-hidden="true">
              <line x1="0" y1="4" x2="26" y2="4" stroke={colours.ghost} strokeWidth="1.6" strokeDasharray="4 4" />
            </svg>
            previous {days} days
          </span>
        </div>
        <div className="card-body">
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={series} margin={{ top: 8, right: 10, bottom: 0, left: -6 }}>
              <CartesianGrid stroke={colours.grid} strokeDasharray="2 4" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={shortDate}
                tick={{ fontSize: 11, fill: colours.axis }}
                axisLine={{ stroke: colours.grid }}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={42}
              />
              <YAxis
                tickFormatter={axisFormat}
                tick={{ fontSize: 11, fill: colours.axis }}
                axisLine={false}
                tickLine={false}
                width={56}
              />
              <Tooltip
                labelFormatter={shortDate}
                formatter={(v, name) => [
                  fmt(metric, v),
                  name === 'prev' ? `Previous ${days} days` : labelOf(labels, metric),
                ]}
                contentStyle={{
                  background: 'var(--surface)',
                  border: `1px solid ${colours.grid}`,
                  borderRadius: 6,
                  fontSize: 12,
                  fontFamily: 'var(--font-body)',
                }}
              />
              <Area type="monotone" dataKey={metric} stroke="none" fill={colours.line} fillOpacity={0.11} />
              <Line type="monotone" dataKey="prev" stroke={colours.ghost} strokeWidth={1.6}
                    strokeDasharray="4 4" dot={false} />
              <Line type="monotone" dataKey={metric} stroke={colours.line} strokeWidth={2.2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Campaign performance</span>
          <span className="card-note">
            {byCampaign.length} active · sorted by {labelOf(labels, sortKey).toLowerCase()}
          </span>
        </div>
        <div className="tbl-wrap">
          <table className="data-table dense">
            <thead>
              <tr>
                <th>Campaign</th>
                <th className="trend-cell">Trend</th>
                {TABLE_COLUMNS.map((k) => (
                  <th
                    key={k}
                    className="sortable"
                    aria-sort={sortKey === k
                      ? (LOWER_IS_BETTER.has(k) ? 'ascending' : 'descending')
                      : undefined}
                  >
                    <button onClick={() => setSortKey(k)}>
                      {labelOf(labels, k)}
                      <Icon
                        name={sortKey === k && LOWER_IS_BETTER.has(k) ? 'up' : 'down'}
                        size={11}
                        strokeWidth={3}
                      />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {byCampaign.map((r) => (
                <CampaignRow
                  key={r.campaign_name}
                  row={r}
                  trend={
                    // null until the series for THIS selection have landed
                    // whole. A cut series draws a confidently wrong trend, so
                    // the row draws nothing rather than guess - which is what
                    // it already did before the query was deferred.
                    sparklines
                      && sparklines.key === sparklineKey
                      && sparklines.trendsComplete
                      ? (sparklines.trends.get(r.campaign_name) || [])
                        .map((d) => d[metric] ?? 0)
                      : null
                  }
                  labels={labels}
                  days={days}
                  metric={metric}
                  sortKey={sortKey}
                  sortMax={sortMax}
                  colour={colours.line}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function CampaignRow({ row, trend, labels, days, metric, sortKey, sortMax, colour }) {
  // The trend arrives with the rest of the data. It used to be a query per
  // row - free against generated data, a dozen round trips to BigQuery once
  // the data is real.
  //
  // A null trend means the campaign-by-date result may have been truncated, so
  // nothing is drawn. A sparkline from a cut series is a wrong trend drawn
  // confidently, which is worse than an empty cell.
  return (
    <tr>
      <td style={{ fontWeight: 500 }}>{row.campaign_name}</td>
      <td className="trend-cell">
        {trend ? <Sparkline series={trend} colour={colour} /> : null}
      </td>
      {TABLE_COLUMNS.map((k) => {
        const sorted = k === sortKey;
        const width = sorted && sortMax > 0
          ? Math.max(3, ((row[k] || 0) / sortMax) * 100)
          : 0;
        return (
          <td key={k} className={`num${sorted ? ' mag' : ''}`} style={k === 'conversions' ? { fontWeight: 600 } : undefined}>
            {sorted && <span className="bar" style={{ width: `calc(${width}% - 13px)` }} />}
            <span className="v">{cell(k, row[k])}</span>
          </td>
        );
      })}
    </tr>
  );
}

function cell(key, value) {
  if (key === 'spend') return gbp(value);
  if (key === 'ctr') return pct(value);
  if (key === 'roas') return value ? `${value.toFixed(2)}×` : '—';
  if (key === 'cpa') return fmt('cpa', value);
  return num(value);
}